/**
 * @file index.tsx
 *
 * @fileoverview Component for featured job videos.
 */
import React, { useContext } from 'react';
import { FeaturedJobsParagraph, Job } from '~/jobiqo/types/types';
import Link from '~/jobiqo/components/Link';
import { LinkButton } from '~/vcl/atoms/01-forms/01-button/02-link-button';
import { AddIcon } from '~/vcl/icons/Add';
import { FormattedMessage } from 'react-intl';
import { getFrontendTheme } from '~/jobiqo/utils/getFrontend';
import { Badge } from '~/vcl/atoms/03-badge';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { Box } from '~/vcl/atoms/07-box';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import FlexItem from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { HideMobile } from '~/vcl/atoms/06-layout/02-visibility/02-hide-mobile';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import { HideDesktop } from '~/vcl/atoms/06-layout/02-visibility/01-hide-desktop';
import { extractServiceFromUrl } from '~/jobiqo/utils/links';
import { checkVideoConsent, mapVideoTypeToService } from '../utils/landingPage';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { PopoverList } from '~/vcl/molecules/13-popover-list';
import EmbeddedVideo from '../EmbeddedYoutube/components/EmbeddedVideo';
import { AlignedCenter } from '~/vcl/atoms/06-layout/01-alignment/01-align-center';
import CMPServiceCheckbox from '../../CMPserviceCheckbox';
import styles from './videoJobs.module.scss';
import { getJobLocationToRender } from '~/jobiqo/services/jobs';
import { getOrgProfileFromJob } from '../../Job/utils';
import NextLink from 'next/link';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

const ShowAllJobsButton = ({ frontendTheme }) => {
  const { JOBS } = useFrontendConfig();

  return (
    <Link href={JOBS.JOBS_PAGE} prefetch={false} asChild>
      <LinkButton
        linkElement={NextLink}
        buttonStyle={
          frontendTheme.LANDING_PAGES.VIDEO_JOB_TEASERS.SHOW_ALL_BUTTON_STYLE
        }
        raised={false}
      >
        <AddIcon title="Show more" />
        <span>
          <FormattedMessage
            id="landing_jobs_link"
            defaultMessage="See all jobs"
          />
        </span>
      </LinkButton>
    </Link>
  );
};

interface LandingPageVideoJobsProps {
  /**
   * Top intro text for display on the landing page row.
   */
  rowFeaturedJobsInfo: FeaturedJobsParagraph;
  /**
   * The size for the container.
   */
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
}

export const LandingPageVideoJobs = ({
  rowFeaturedJobsInfo,
  maxContainerSize,
}: LandingPageVideoJobsProps) => {
  const frontendConfig = useFrontendConfig();
  const frontendTheme = getFrontendTheme(frontendConfig);
  const userContext: UserContextInterface = useContext(UserContext);

  const ParagraphInfo = ({ children }) =>
    frontendTheme.LANDING_PAGES.VIDEO_JOB_TEASERS.INTRO_BADGE ? (
      <Badge>{children}</Badge>
    ) : (
      <Text
        color={
          frontendTheme.LANDING_PAGES.VIDEO_JOB_TEASERS.INTRO_COLOR ?? 'primary'
        }
        fontWeight="bold"
        textTransform="uppercase"
      >
        {children}
      </Text>
    );

  if (rowFeaturedJobsInfo?.jobdata?.pages?.length === 0) {
    // If we have no jobs don't show the component.
    return null;
  }

  return (
    <Box
      boxShadow="none"
      borderRadius="0px"
      margin={0}
      padding={0}
      backgroundColorOverride={
        rowFeaturedJobsInfo.background
          ? `var(--color-${rowFeaturedJobsInfo.background})`
          : 'transparent'
      }
      data-test-id={rowFeaturedJobsInfo.bundle}
    >
      <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
        <Spacing
          paddingTop={rowFeaturedJobsInfo.spacing ?? 11}
          paddingBottom={rowFeaturedJobsInfo.spacing ?? 12}
          margin={0}
        >
          <FlexItems justifyContent="space-between" alignItems="center">
            <FlexItem>
              {rowFeaturedJobsInfo.introText && (
                <>
                  <ParagraphInfo>{rowFeaturedJobsInfo.introText}</ParagraphInfo>
                  <Spacing size="medium" />
                </>
              )}
              {rowFeaturedJobsInfo.introTextSecondary && (
                <>
                  <Text
                    tag="h2"
                    fontSize={[5, 6]}
                    lineHeight={[5, 6]}
                    {...(frontendTheme.LANDING_PAGES.JOB_TEASERS
                      .INTRO_SECONDARY_COLOR
                      ? {
                          color:
                            frontendTheme.LANDING_PAGES.JOB_TEASERS
                              .INTRO_SECONDARY_COLOR,
                        }
                      : null)}
                  >
                    {rowFeaturedJobsInfo.introTextSecondary}
                  </Text>
                  <Spacing size="large" />
                </>
              )}
            </FlexItem>
            <HideMobile>
              <FlexItem justifySelf="flex-end">
                <ShowAllJobsButton frontendTheme={frontendTheme} />
              </FlexItem>
            </HideMobile>
          </FlexItems>
          <Grid columns={[1, 3]}>
            {(rowFeaturedJobsInfo?.jobdata?.pages as any).map(
              (item: Job, index: number) => {
                let consented = true;
                let service = '';
                if (item['videoUrl']) {
                  service = extractServiceFromUrl(item['videoUrl']);
                  consented = checkVideoConsent(service, userContext);
                }
                const orgProfile = getOrgProfileFromJob(item);

                return (
                  <Cell display="grid" key={index}>
                    <Box className={styles.cardBox}>
                      <div>
                        {item['videoUrl'] && (
                          <FlexItem flexGrow={5}>
                            {item['videoUrl'] && consented ? (
                              <Cell gridColumn={'span 3'}>
                                <FlexItems justifyContent="center">
                                  <EmbeddedVideo
                                    link={{
                                      text: '',
                                      url: item['videoUrl'],
                                    }}
                                  />
                                </FlexItems>
                              </Cell>
                            ) : (
                              <AlignedCenter>
                                <Text marginTop="0">
                                  <FormattedMessage
                                    id="video_teasers_row_consent"
                                    defaultMessage="Consent to this service"
                                  />
                                </Text>
                                <Spacing />
                                <CMPServiceCheckbox
                                  serviceId={mapVideoTypeToService(service)}
                                />
                              </AlignedCenter>
                            )}
                          </FlexItem>
                        )}
                        <Spacing marginTop={5} marginBottom={5}>
                          {item.title && (
                            <Text fontSize={3} marginLeft={5}>
                              <Link href={item.url.path}>{item.title}</Link>
                            </Text>
                          )}
                          {orgProfile && (
                            <Text
                              fontSize={2}
                              marginLeft={5}
                              color="gray4"
                              marginTop={3}
                            >
                              <Link
                                href={
                                  item.organizationProfile?.urlNoPrefix ??
                                  item.organizationProfile?.url?.path ??
                                  ''
                                }
                              >
                                {orgProfile.label}
                              </Link>
                            </Text>
                          )}
                          {item.address && (
                            <Text
                              tag="div"
                              fontSize={2}
                              marginLeft={5}
                              marginTop={3}
                              color="gray4"
                            >
                              <PopoverList
                                items={getJobLocationToRender(item)}
                                iconGap={0}
                              />
                            </Text>
                          )}
                        </Spacing>
                      </div>
                    </Box>
                  </Cell>
                );
              },
            )}
          </Grid>
          <HideDesktop>
            <Spacing />
            <ShowAllJobsButton frontendTheme={frontendTheme} />
          </HideDesktop>
        </Spacing>
      </Main>
    </Box>
  );
};
export default LandingPageVideoJobs;
